<template>
  <div>
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <h1>{{ $t('security_pending_contracts') }}</h1>
          <p>Revisa tus contratos y solicitudes de empresas clientes y {{ locationNamePlural }}.</p>
        </v-flex>
      </v-layout>

      <!-- Loading State -->
      <v-layout row wrap v-if="isLoading">
        <v-flex xs12>
          <v-progress-linear indeterminate></v-progress-linear>
          <p class="mt-3">{{ $t('loading') }}...</p>
        </v-flex>
      </v-layout>

      <!-- Error State -->
      <v-layout row wrap v-if="securityError">
        <v-flex xs12>
          <v-alert type="error" dismissible class="mb-3">
            {{ securityError }}
          </v-alert>
        </v-flex>
      </v-layout>

      <!-- ============================================= -->
      <!-- SOLICITUDES DE EMPRESAS CLIENTES (PENDIENTES) -->
      <!-- ============================================= -->
      <v-layout row wrap v-if="!isLoading && pendingContracts && pendingContracts.length > 0">
        <v-flex xs12>
          <h2 class="primary--text mb-3">Solicitudes de Empresas Clientes</h2>
        </v-flex>
        <v-flex xs12>
          <v-card v-for="contract in pendingContracts" :key="`company-${contract.id}`" class="elevation-3 mb-4">
            <v-toolbar dark color="warning" class="mb-3">
              <v-toolbar-title>{{ contract.host_name }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-chip color="warning" text-color="white" small>
                {{ $t('security_pending') }}
              </v-chip>
            </v-toolbar>

            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12 md6>
                  <h3 class="mb-3">{{ $t('security_company_info') }}</h3>
                  <v-list dense>
                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Empresa Cliente</v-list-tile-title>
                        <v-list-tile-sub-title>{{ contract.host_name }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>Proveedor de Seguridad</v-list-tile-title>
                        <v-list-tile-sub-title>{{ contract.security_name }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>

                    <v-list-tile>
                      <v-list-tile-content>
                        <v-list-tile-title>{{ $t('created_at') }}</v-list-tile-title>
                        <v-list-tile-sub-title>{{ formatDate(contract.created_at) }}</v-list-tile-sub-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </v-list>
                </v-flex>

                <v-flex xs12 md6>
                  <h3 class="mb-3">Acción Requerida</h3>
                  <p class="mb-4 grey--text">
                    {{ contract.host_name }} ha solicitado un contrato de seguridad con tu empresa.
                    Puedes aceptar o rechazar esta solicitud.
                  </p>
                  <v-layout row wrap>
                    <v-flex xs12 sm6 class="pr-2">
                      <v-btn
                        color="success"
                        block
                        :loading="respondingContractId === contract.id"
                        @click="respondToContract(contract.id, 'accept')"
                      >
                        <v-icon left>check_circle</v-icon>
                        Aceptar
                      </v-btn>
                    </v-flex>
                    <v-flex xs12 sm6 class="pl-2">
                      <v-btn
                        color="error"
                        block
                        :loading="respondingContractId === contract.id"
                        @click="respondToContract(contract.id, 'reject')"
                      >
                        <v-icon left>cancel</v-icon>
                        Rechazar
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- ============================================= -->
      <!-- SOLICITUDES / CONTRATOS DE PARQUES INDUSTRIALES -->
      <!-- ============================================= -->
      <v-layout row wrap v-if="!isLoadingParks && parkContracts.length > 0" class="mt-3">
        <v-flex xs12>
          <h2 class="primary--text mb-3">{{ locationNameCapitalized }}</h2>
        </v-flex>

        <v-flex xs12 sm6 v-for="park in parkContracts" :key="`park-${park.id}`" class="pa-2">
          <v-card class="elevation-3 rounded-lg" :style="parkCardBorderStyle(park.status)">
            <v-card-text class="pa-4">
              <h3 class="subheading font-weight-bold mb-2">{{ park.park_nombre || park.nombre }}</h3>

              <v-chip :color="getParkStatusColor(park.status)" text-color="white" small class="mb-3 ml-0">
                <v-icon left small>{{ getParkStatusIcon(park.status) }}</v-icon>
                {{ park.status_display || park.status }}
              </v-chip>

              <div v-if="park.descripcion" class="mb-3">
                <small class="grey--text text-uppercase">Descripción</small>
                <p class="body-2 mb-0">{{ park.descripcion }}</p>
              </div>

              <div class="mb-2">
                <small class="grey--text text-uppercase">{{ park.status === 'PENDING' ? 'Solicitado' : 'Desde' }}</small>
                <p class="body-2 font-weight-bold mb-0">{{ formatDate(park.created_at) }}</p>
              </div>

              <!-- Acción requerida: solo si está pendiente -->
              <template v-if="park.status === 'PENDING'">
                <v-alert type="info" :value="true" dense class="mb-3 caption">
                  Este {{ locationName }} solicitó tus servicios de seguridad.
                </v-alert>
                <div class="action-column">
                  <v-btn
                    color="success"
                    depressed
                    class="ma-0 mb-2"
                    :loading="respondingParkContractId === park.id"
                    @click="respondToParkContract(park.id, 'accept')"
                  >
                    <v-icon left small>check_circle</v-icon>
                    Aceptar
                  </v-btn>
                  <v-btn
                    color="error"
                    outline
                    class="ma-0"
                    :loading="respondingParkContractId === park.id"
                    @click="respondToParkContract(park.id, 'reject')"
                  >
                    <v-icon left small>cancel</v-icon>
                    Rechazar
                  </v-btn>
                </div>
              </template>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>

      <!-- No Contracts Message -->
      <v-layout row wrap v-if="!isLoading && !isLoadingParks && (!pendingContracts || pendingContracts.length === 0) && parkContracts.length === 0">
        <v-flex xs12>
          <v-alert type="info" :value="true">
            No tienes contratos ni solicitudes pendientes de aprobación en este momento.
          </v-alert>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { FETCH_PENDING_CONTRACTS, RESPOND_TO_CONTRACT, FETCH_MY_PARKS, RESPOND_TO_PARK_CONTRACT } from '../../../store/modules/security'
import { USER_REQUEST } from '../../../store/actions/user'
import { mapGetters } from 'vuex'

export default {
  name: 'SecurityPendingContracts',
  data() {
    return {
      respondingContractId: null,
      respondingParkContractId: null,
      isLoadingParks: false
    }
  },
  computed: {
    ...mapGetters(['getToken', 'getProfile']),
    ...mapGetters('security', [
      'isSecurityLoading',
      'getSecurityError',
      'getPendingContracts',
      'getMyParks'
    ]),
    isLoading() {
      return this.isSecurityLoading
    },
    securityError() {
      return this.getSecurityError
    },
    pendingContracts() {
      return this.getPendingContracts
    },
    parkContracts() {
      return this.getMyParks || []
    },
    locationName() {
      const profile = this.getProfile || {}
      return profile.is_building ? 'edificio corporativo' : 'parque'
    },
    locationNamePlural() {
      const profile = this.getProfile || {}
      return profile.is_building ? 'edificios corporativos' : 'parques industriales'
    },
    locationNameCapitalized() {
      const profile = this.getProfile || {}
      return profile.is_building ? 'Edificios Corporativos' : 'Parques Industriales'
    }
  },
  methods: {
    fetchPendingContracts() {
      const token = this.getToken
      this.$store.dispatch(`security/${FETCH_PENDING_CONTRACTS}`, token)
    },
    fetchMyParks() {
      this.isLoadingParks = true
      this.$store.dispatch(`security/${FETCH_MY_PARKS}`, this.getToken)
        .finally(() => {
          this.isLoadingParks = false
        })
    },
    respondToContract(contractId, action) {
      this.respondingContractId = contractId
      this.$store.dispatch(`security/${RESPOND_TO_CONTRACT}`, {
        token: this.getToken,
        contractId: contractId,
        action: action
      })
      .then(() => {
        const actionText = action === 'accept' ? 'aceptado' : 'rechazado'
        this.$store.commit('snackbar_change_message', `Contrato ${actionText} correctamente`)
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchPendingContracts()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', 'Error al responder al contrato')
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
      .finally(() => {
        this.respondingContractId = null
      })
    },
    respondToParkContract(contractId, action) {
      this.respondingParkContractId = contractId
      this.$store.dispatch(`security/${RESPOND_TO_PARK_CONTRACT}`, {
        token: this.getToken,
        contractId: contractId,
        action: action
      })
      .then(() => {
        const actionText = action === 'accept' ? 'aceptada' : 'rechazada'
        this.$store.commit('snackbar_change_message', `Solicitud del ${this.locationName} ${actionText} correctamente`)
        this.$store.commit('snackbar_change_color', 'success')
        this.$store.commit('snackbar_activate_true')
        this.fetchMyParks()
      })
      .catch(err => {
        this.$store.commit('snackbar_change_message', `Error al responder a la solicitud del ${this.locationName}`)
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      })
      .finally(() => {
        this.respondingParkContractId = null
      })
    },
    getParkStatusColor(status) {
      const colors = {
        'ACTIVE': 'success',
        'PENDING': 'warning',
        'REJECTED': 'error',
        'INACTIVE': 'grey'
      }
      return colors[status] || 'grey'
    },
    getParkStatusIcon(status) {
      const icons = {
        'ACTIVE': 'check_circle',
        'PENDING': 'schedule',
        'REJECTED': 'cancel',
        'INACTIVE': 'block'
      }
      return icons[status] || 'help'
    },
    parkCardBorderStyle(status) {
      const borderColors = {
        'ACTIVE': '#4CAF50',
        'PENDING': '#FFC107',
        'REJECTED': '#F44336',
        'INACTIVE': '#9E9E9E'
      }
      return `border-left: 4px solid ${borderColors[status] || '#9E9E9E'};`
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString(this.$i18n.locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }

    // Esperar a que el perfil esté cargado antes de cargar datos
    if (!this.$store.getters.isProfileLoaded) {
      this.$store.dispatch(USER_REQUEST)
        .then(() => {
          this.fetchPendingContracts()
          this.fetchMyParks()
        })
        .catch(() => {
          this.$router.push('/')
        })
    } else {
      this.fetchPendingContracts()
      this.fetchMyParks()
    }
  }
}
</script>

<style scoped>
.mb-4 {
  margin-bottom: 30px;
}
.mb-3 {
  margin-bottom: 20px;
}
.mt-3 {
  margin-top: 20px;
}
.pr-2 {
  padding-right: 10px;
}
.pl-2 {
  padding-left: 10px;
}
.action-column {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.action-column .v-btn {
  width: 100%;
}
</style>
